/*
 * Cottomix theme — core behaviour (theme.js)
 *
 * Purpose: the single vanilla-JS runtime of the theme. No dependencies, no
 * build step. Loaded with defer, after the inline script that defines
 * window.CMConfig. Registers two custom elements (<cm-drawer>, <cm-modal>)
 * and creates exactly one global: window.CM.
 *
 * Public API (window.CM):
 *   on(name, fn) -> unsubscribe   off(name, fn)   emit(name, detail)
 *       Event bus; emit also dispatches CustomEvent 'cm:<name>' on document.
 *   fetchSection(url, ids)   parseHTML(html)
 *       Shopify Section Rendering API helpers.
 *   cart.get()   cart.add(items, options)   cart.change(line, quantity, options)
 *       Cart AJAX API; emits cart:get / cart:add / cart:change / cart:updated / cart:error.
 *   lockScroll()   unlockScroll()
 *       Document scroll lock with scrollbar compensation (--cm-scrollbar-width).
 *   trapFocus(container, options) -> release()
 *       Keyboard focus trap used by overlays.
 *   open(id)   close(id)   closeAll()
 *       Control <cm-drawer> / <cm-modal> overlays by id.
 *   reveal.init(root)
 *       Scroll reveal for .cm-reveal / .cm-fade elements (--cm-order
 *       stagger) and [data-cm-highlight] hooks (is-visible after
 *       data-highlight-delay ms, default 700).
 *   header.update()
 *       Re-measure sticky header metrics (--cm-header-height / --cm-header-bottom
 *       / --cm-header-stack-height = whole header group; --cm-header-follow is
 *       the collapsible copy that drops to 0px while the header is hidden).
 *   config
 *       The window.CMConfig object as read at load time.
 */
(function () {
  'use strict';

  if (window.CM && window.CM.__loaded) return;

  const CFG = window.CMConfig || {};
  const ROUTES = Object.assign({
    cart_url: '/cart',
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_update_url: '/cart/update',
    root_url: '/'
  }, CFG.routes || {});
  const STRINGS = Object.assign({
    close: 'Close',
    loading: 'Loading',
    error: 'Something went wrong. Please try again.'
  }, CFG.strings || {});
  const MOTION = CFG.motion !== false;
  const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------ *
   * Part 1 — event bus
   * ------------------------------------------------------------ */
  const handlers = new Map();

  function on(name, fn) {
    let set = handlers.get(name);
    if (!set) {
      set = new Set();
      handlers.set(name, set);
    }
    set.add(fn);
    return function unsubscribe() {
      off(name, fn);
    };
  }

  function off(name, fn) {
    const set = handlers.get(name);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) handlers.delete(name);
  }

  function emit(name, detail) {
    const set = handlers.get(name);
    if (set) {
      set.forEach(function (fn) {
        try {
          fn(detail);
        } catch (err) {
          if (window.CMConfig && window.CMConfig.debug) { console.error('[CM] handler for "' + name + '" failed:', err); }
        }
      });
    }
    document.dispatchEvent(new CustomEvent('cm:' + name, { detail: detail }));
  }

  /* ------------------------------------------------------------ *
   * Part 2 — scroll lock
   * ------------------------------------------------------------ */
  let lockCount = 0;

  function lockScroll() {
    lockCount += 1;
    if (lockCount === 1) {
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--cm-scrollbar-width', scrollbar + 'px');
      document.documentElement.classList.add('cm-scroll-locked');
    }
  }

  function unlockScroll() {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount === 0) {
      document.documentElement.classList.remove('cm-scroll-locked');
      document.documentElement.style.removeProperty('--cm-scrollbar-width');
    }
  }

  /* ------------------------------------------------------------ *
   * Part 3 — focus trap
   * ------------------------------------------------------------ */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

  function isFocusableVisible(el) {
    if (el.inert) return false;
    if (el.closest && el.closest('[inert]')) return false;
    return el.offsetParent !== null || el.getClientRects().length > 0;
  }

  function getFocusable(container) {
    const found = container.querySelectorAll(FOCUSABLE);
    const result = [];
    for (let i = 0; i < found.length; i += 1) {
      if (isFocusableVisible(found[i])) result.push(found[i]);
    }
    return result;
  }

  function focusElement(el) {
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      el.focus();
    }
  }

  function trapFocus(container, options) {
    const opts = options || {};
    const previous = document.activeElement;
    let released = false;

    let target = opts.initialFocus || null;
    if (!target) {
      const focusables = getFocusable(container);
      target = focusables.length > 0 ? focusables[0] : container;
    }
    if (target === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    focusElement(target);
    if (!container.contains(document.activeElement)) {
      let tries = 0;
      (function retry() {
        if (released || container.contains(document.activeElement) || tries >= 5) return;
        tries += 1;
        requestAnimationFrame(function () {
          if (released || container.contains(document.activeElement)) return;
          focusElement(target);
          retry();
        });
      })();
    }

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        event.preventDefault();
        focusElement(container);
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        focusElement(first);
      }
    }

    let refocusing = false;
    function onFocusin(event) {
      if (!container.isConnected) {
        release();
        return;
      }
      // A failed focus() re-entrantly dispatches focusin (WebKit drops focus on
      // body when the target is still hidden by the open transition). Re-focusing
      // from inside that dispatch would recurse on every failure until the stack
      // overflows, so never re-enter while a programmatic focus is in flight.
      if (refocusing) return;
      if (!container.contains(event.target)) {
        const focusables = getFocusable(container);
        refocusing = true;
        try {
          focusElement(focusables.length > 0 ? focusables[0] : container);
        } finally {
          refocusing = false;
        }
      }
    }

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('focusin', onFocusin, true);

    return function release() {
      if (released) return;
      released = true;
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('focusin', onFocusin, true);
      if (opts.returnFocus !== false && previous && previous.isConnected && typeof previous.focus === 'function') {
        focusElement(previous);
      }
    };
  }

  /* ------------------------------------------------------------ *
   * Part 4 — network helpers (Cart AJAX + Section Rendering API)
   * ------------------------------------------------------------ */
  async function request(url, init) {
    const res = await fetch(url, init);
    let data = {};
    try {
      data = await res.json();
    } catch (err) {
      data = {};
    }
    const nestedBad = data && typeof data.status === 'number' && data.status >= 400;
    if (!res.ok || nestedBad) {
      const err = new Error((data && (data.description || data.message)) || STRINGS.error);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  function fetchSection(url, ids) {
    const list = (Array.isArray(ids) ? ids : [ids]).map(function (id) {
      return String(id);
    });
    // Keep the page's own query string (preview_theme_id / country / language) so the
    // sections are rendered by the same theme + context this page was served with.
    const base = url + (window.location.search || '');
    const finalUrl = base + (base.indexOf('?') === -1 ? '?' : '&') + 'sections=' + list.map(function (id) {
      return encodeURIComponent(id);
    }).join(',');
    // No Accept: application/json here — on an HTML route that header makes Shopify
    // skip the Section Rendering API entirely and answer with the route's plain JSON
    // (e.g. {"product": …}), so the sections map never arrives.
    return request(finalUrl, {
      method: 'GET',
      credentials: 'same-origin'
    });
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  const cart = {
    get: function () {
      return request(ROUTES.cart_url + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then(function (data) {
        CM.emit('cart:get', data);
        return data;
      });
    },
    add: function (items, options) {
      const opts = options || {};
      const sections = opts.sections || [];
      const list = (Array.isArray(items) ? items : [items]).map(function (item) {
        return Object.assign({ quantity: 1 }, item || {});
      });
      const body = { items: list };
      if (sections.length > 0) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      return request(ROUTES.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      }).then(function (res) {
        CM.emit('cart:add', res);
        CM.emit('cart:updated', { cart: res });
        return res;
      }).catch(function (err) {
        CM.emit('cart:error', { message: err.message, status: err.status });
        throw err;
      });
    },
    change: function (line, quantity, options) {
      const opts = options || {};
      const sections = opts.sections || [];
      const key = typeof line === 'string' && isNaN(Number(line)) ? 'id' : 'line';
      const body = { quantity: quantity };
      body[key] = line;
      if (sections.length > 0) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      return request(ROUTES.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      }).then(function (res) {
        CM.emit('cart:change', res);
        CM.emit('cart:updated', { cart: res });
        return res;
      }).catch(function (err) {
        CM.emit('cart:error', { message: err.message, status: err.status });
        throw err;
      });
    }
  };

  /* ------------------------------------------------------------ *
   * Part 5 — overlay custom elements (<cm-drawer>, <cm-modal>)
   * ------------------------------------------------------------ */
  const openStack = [];

  // body.drawer-open mirrors the overlay stack (CSS uses it to hide the
  // WhatsApp FAB / mobile bar while any drawer or modal is open).
  function syncBodyDrawerOpen() {
    if (openStack.length === 0) document.body.classList.remove('drawer-open');
    else document.body.classList.add('drawer-open');
  }

  function readMs(value, fallback) {
    const raw = String(value == null ? '' : value).trim();
    const match = raw.match(/^([\d.]+)\s*(ms|s)?$/);
    if (!match) return fallback;
    const num = parseFloat(match[1]);
    if (isNaN(num)) return fallback;
    return match[2] === 's' ? num * 1000 : num;
  }

  function syncTriggers(el, expanded) {
    if (!el.id) return;
    const triggers = document.querySelectorAll('[data-cm-open="' + el.id + '"]');
    for (let i = 0; i < triggers.length; i += 1) {
      triggers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  class CMOverlay extends HTMLElement {
    connectedCallback() {
      if (this._cmReady) return;
      this._cmReady = true;
      const cfg = this.constructor.cmConfig || {};
      const panel = this.querySelector(cfg.panelSelector || '.cm-drawer__panel');
      this._panel = panel;
      if (panel) {
        if (!panel.hasAttribute('role')) panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        if (!panel.hasAttribute('aria-label') && !panel.hasAttribute('aria-labelledby')) {
          const label = this.getAttribute('aria-label');
          if (label) panel.setAttribute('aria-label', label);
        }
      }
      this._onOverlayClick = function (event) {
        const target = event.target;
        const closer = target && target.closest ? target.closest('[data-cm-close]') : null;
        // The backdrop itself closes too, even without data-cm-close on it.
        const onBackdrop = !!(target && target.classList &&
          (target.classList.contains('cm-drawer__overlay') || target.classList.contains('cm-modal__overlay')));
        if ((closer && this.contains(closer)) || onBackdrop) this.close();
      }.bind(this);
      this.addEventListener('click', this._onOverlayClick);
      if (this.hasAttribute('open')) {
        this._opener = null;
        CM.lockScroll();
        this._release = CM.trapFocus(this._panel || this, { returnFocus: false });
        openStack.push(this);
        syncBodyDrawerOpen();
        syncTriggers(this, true);
      }
    }

    open() {
      if (this.hasAttribute('open')) return;
      const cfg = this.constructor.cmConfig || {};
      this._opener = document.activeElement;
      this.setAttribute('data-cm-seen', '');
      this.setAttribute('open', '');
      CM.lockScroll();
      this._release = CM.trapFocus(this._panel || this, { returnFocus: false });
      openStack.push(this);
      syncBodyDrawerOpen();
      syncTriggers(this, true);
      CM.emit(cfg.kind + ':open', { id: this.id, el: this });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      const cfg = this.constructor.cmConfig || {};
      this.removeAttribute('open');
      if (this._release) {
        this._release();
        this._release = null;
      }
      const opener = this._opener;
      this._opener = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') {
        focusElement(opener);
      }
      const idx = openStack.indexOf(this);
      if (idx !== -1) openStack.splice(idx, 1);
      syncBodyDrawerOpen();
      syncTriggers(this, false);
      const ms = (REDUCED || !MOTION)
        ? 0
        : readMs(getComputedStyle(this).getPropertyValue(cfg.motionVar), cfg.fallbackMs);
      this._clearUnlockTimer();
      this._unlockTimer = setTimeout(function () {
        this._unlockTimer = null;
        CM.unlockScroll();
      }.bind(this), ms);
      CM.emit(cfg.kind + ':close', { id: this.id, el: this });
    }

    toggle() {
      if (this.hasAttribute('open')) this.close();
      else this.open();
    }

    disconnectedCallback() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      if (this._release) {
        this._release();
        this._release = null;
      }
      const idx = openStack.indexOf(this);
      if (idx !== -1) openStack.splice(idx, 1);
      syncBodyDrawerOpen();
      syncTriggers(this, false);
      this._clearUnlockTimer();
      CM.unlockScroll();
    }

    _clearUnlockTimer() {
      if (this._unlockTimer) {
        clearTimeout(this._unlockTimer);
        this._unlockTimer = null;
      }
    }
  }

  class CMDrawer extends CMOverlay {}
  class CMModal extends CMOverlay {}
  CMDrawer.cmConfig = { kind: 'drawer', motionVar: '--cm-motion-drawer', panelSelector: '.cm-drawer__panel', fallbackMs: 500 };
  CMModal.cmConfig = { kind: 'modal', motionVar: '--cm-motion-modal', panelSelector: '.cm-modal__panel', fallbackMs: 300 };

  if (!customElements.get('cm-drawer')) customElements.define('cm-drawer', CMDrawer);
  if (!customElements.get('cm-modal')) customElements.define('cm-modal', CMModal);

  document.addEventListener('click', function (event) {
    const trigger = event.target && event.target.closest ? event.target.closest('[data-cm-open]') : null;
    if (!trigger) return;
    event.preventDefault();
    const el = document.getElementById(trigger.getAttribute('data-cm-open'));
    if (el && typeof el.open === 'function') {
      if (el.hasAttribute('open')) {
        if (typeof el.close === 'function') el.close();
      } else {
        el.open();
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || openStack.length === 0) return;
    event.preventDefault();
    const top = openStack[openStack.length - 1];
    if (top && typeof top.close === 'function') top.close();
  });

  function open(id) {
    const el = document.getElementById(id);
    if (el && typeof el.open === 'function') el.open();
    return el;
  }

  function close(id) {
    const el = document.getElementById(id);
    if (el && typeof el.close === 'function') el.close();
    return el;
  }

  function closeAll() {
    openStack.slice().forEach(function (el) {
      if (typeof el.close === 'function') el.close();
    });
  }

  /* ------------------------------------------------------------ *
   * Part 6 — scroll reveal
   * Jlood global observer (codemap hero-sections §11), re-timed to the
   * fid-e4 / QC-OPUS-R1 #8 budget: rootMargin '0px 0px 0px 0px' +
   * threshold 0.1 — reveal starts as soon as 10% of the element is visible,
   * no delay margin. Reveal once + unobserve. Two families:
   *   .cm-reveal = anm-reveal-item (cards/grids, shared card-entry tokens:
   *                opacity 300ms / rise 640ms, stagger min(i,3) * 60ms)
   *   .cm-fade   = anm-fade-element (text, 350ms two-ease)
   * Elements taller than the viewport can never reach ratio .1 — they are
   * revealed on the first visible pixel instead (spec: per-child reveal,
   * never require 100% visibility). Cards inside a horizontal rail reveal
   * as a whole rail when the rail enters, so off-track slides never sit
   * hidden waiting for a carousel swipe. [data-cm-highlight] elements get
   * .is-visible after their data-highlight-delay ms (default 700) so
   * highlight markers wipe after the text lands. Elements that start below
   * the fold (or inside a hidden / content-visibility-skipped subtree) are
   * pre-hidden by JS (data-cm-pre, Gate 0: the stylesheet itself never
   * hides, so above-the-fold content paints immediately and LCP never waits
   * for JS). Unit J: ONE IntersectionObserver does everything, including the
   * one-time above/below-the-fold classification (first entry, rect comes
   * from the entry), so the code never reads layout (no gBCR / offset*);
   * all class toggles are flushed in a single rAF; a MutationObserver scans
   * only the ADDED subtrees; prefers-reduced-motion skips pre-hiding. A 2.5s
   * safety pass after load re-observes anything still pre-hidden with the
   * "any visible pixel reveals" rule so no content can stay faded.
   * ------------------------------------------------------------ */
  const REVEAL_SELECTOR = '.cm-reveal:not(.is-visible), .cm-fade:not(.is-visible), [data-cm-highlight]:not(.is-visible)';
  let revealObserver = null;
  let revealMo = null;
  let revealSafetyArmed = false;
  let revealFrame = 0;
  let revealMoFrame = 0;
  // handed to the observer once, classified on the first IO entry, nudged by
  // the safety pass (any visible pixel reveals) — all membership tests are
  // pure JS, no layout is ever read from the DOM by the reveal code
  const revealKnown = new WeakSet();
  const revealProbed = new WeakSet();
  const revealNudged = new WeakSet();
  const pendingPre = new Set(); // below the fold -> data-cm-pre (flushed in rAF)
  const pendingShow = new Set(); // reveal now -> .is-visible (flushed in rAF)
  const pendingShowPlain = new Set(); // reveal without stagger order (highlights, no-motion)
  const pendingRoots = new Set(); // mutation roots to scan (flushed in rAF)

  function revealRail(el) {
    return el.closest('cm-carousel, .cm-carousel, .cm-rail');
  }

  function scheduleRevealFlush() {
    if (!revealFrame) revealFrame = requestAnimationFrame(flushReveal);
  }

  // The ONLY place the reveal writes to the DOM: one rAF per frame, class
  // toggles only, document-order sort needs no layout.
  function flushReveal() {
    revealFrame = 0;
    pendingPre.forEach(function (el) {
      // a reveal queued in the same frame wins: the element simply shows
      if (!pendingShow.has(el) && !el.classList.contains('is-visible')) {
        el.setAttribute('data-cm-pre', '');
      }
    });
    pendingPre.clear();
    const batch = Array.from(pendingShow);
    pendingShow.clear();
    batch.sort(function (a, b) {
      return (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
    });
    for (let i = 0; i < batch.length; i += 1) {
      const el = batch[i];
      el.style.setProperty('--cm-order', String(Math.min(i, 3)));
      el.classList.add('is-visible');
      if (revealObserver) revealObserver.unobserve(el);
    }
    pendingShowPlain.forEach(function (el) {
      el.classList.add('is-visible');
      if (revealObserver) revealObserver.unobserve(el);
    });
    pendingShowPlain.clear();
  }

  function handleRevealEntries(entries) {
    const rails = new Set();
    const fallbackVh = window.innerHeight || document.documentElement.clientHeight;
    const show = function (el) {
      if (!el || pendingShow.has(el) || el.classList.contains('is-visible')) return;
      pendingShow.add(el);
    };
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const el = entry.target;
      const rect = entry.boundingClientRect;
      const isHighlight = el.hasAttribute('data-cm-highlight');
      if (el.classList.contains('is-visible')) {
        revealObserver.unobserve(el);
        continue;
      }
      // First entry of a candidate = the one-time classification the old code
      // did with getBoundingClientRect. The entry already carries the rect, so
      // this costs no layout. Starting inside (or above) the viewport: stays
      // visible, never hidden, no entry animation (LCP is never delayed).
      // Below the fold or inside a hidden / skipped subtree (zero box):
      // pre-hide and keep watching.
      if (!isHighlight && !revealProbed.has(el)) {
        revealProbed.add(el);
        if (!el.hasAttribute('data-cm-pre')) {
          const vh = entry.rootBounds ? entry.rootBounds.height : fallbackVh;
          const hasBox = rect.width !== 0 || rect.height !== 0;
          if (hasBox && rect.top < vh) {
            revealObserver.unobserve(el);
          } else {
            pendingPre.add(el);
          }
          continue;
        }
      }
      const nudged = revealNudged.has(el);
      revealNudged.delete(el);
      if (!entry.isIntersecting) continue;
      // threshold .1 gates normal elements; elements taller than the viewport
      // top out below .1, so first visible pixel reveals them instead
      if (entry.intersectionRatio < 0.1 && !nudged) {
        const vh = entry.rootBounds ? entry.rootBounds.height : fallbackVh;
        if (rect.height < vh) continue;
      }
      if (isHighlight) {
        revealObserver.unobserve(el);
        const delay = parseInt(el.getAttribute('data-highlight-delay'), 10);
        window.setTimeout(function () {
          pendingShowPlain.add(el);
          scheduleRevealFlush();
        }, isNaN(delay) ? 700 : delay);
        continue;
      }
      show(el);
      // whole-rail reveal: off-track slides of a horizontal carousel reveal
      // with their rail, not one-by-one on swipe (one query per rail per batch)
      const rail = revealRail(el);
      if (rail && !rails.has(rail)) {
        rails.add(rail);
        const hidden = rail.querySelectorAll('.cm-reveal:not(.is-visible)');
        for (let k = 0; k < hidden.length; k += 1) show(hidden[k]);
      }
    }
    if (pendingPre.size || pendingShow.size) scheduleRevealFlush();
  }

  function getRevealObserver() {
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(handleRevealEntries, {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0, 0.1]
      });
    }
    return revealObserver;
  }

  // Safety net (fid-e4 e): load + 2.5s — anything still pre-hidden
  // (data-cm-pre) is re-observed with the "any visible pixel" rule, so a
  // throttled / missed notification can never leave a blank band behind.
  // A fresh observe() always delivers a fresh entry; no rect is read.
  function armRevealSafety() {
    if (revealSafetyArmed) return;
    revealSafetyArmed = true;
    const force = function () {
      window.setTimeout(function () {
        if (!revealObserver) return;
        const pending = document.querySelectorAll('[data-cm-pre]:not(.is-visible)');
        for (let i = 0; i < pending.length; i += 1) {
          revealNudged.add(pending[i]);
          revealObserver.unobserve(pending[i]);
          revealObserver.observe(pending[i]);
        }
      }, 2500);
    };
    if (document.readyState === 'complete') force();
    else window.addEventListener('load', force, { once: true });
  }

  // Jlood applyInViewport replaced (PERFORMANCE, QC-OPUS-R1 30 / Gate 0 /
  // unit J): nothing is measured by the script. Every candidate is handed to
  // ONE IntersectionObserver; its first entry (which already carries the
  // rect) classifies the element: starts in/above the viewport -> stays
  // visible, never hidden, never animated (LCP untouched); below the fold or
  // inside a hidden / skipped (content-visibility) subtree -> data-cm-pre,
  // revealed on entry. Zero getBoundingClientRect / offset* reads, no
  // forced layout; every class toggle is batched in one rAF.
  function initReveal(root) {
    const scope = root || document;
    const found = [];
    if (scope.nodeType === 1 && scope.matches && scope.matches(REVEAL_SELECTOR)) found.push(scope);
    const inner = scope.querySelectorAll ? scope.querySelectorAll(REVEAL_SELECTOR) : [];
    for (let i = 0; i < inner.length; i += 1) found.push(inner[i]);
    const fresh = [];
    for (let i = 0; i < found.length; i += 1) {
      if (revealKnown.has(found[i])) continue;
      revealKnown.add(found[i]);
      fresh.push(found[i]);
    }
    if (REDUCED || !MOTION || !('IntersectionObserver' in window)) {
      fresh.forEach(function (el) { pendingShowPlain.add(el); });
      if (fresh.length) scheduleRevealFlush();
      return;
    }
    watchRevealMutations();
    armRevealSafety();
    if (fresh.length === 0) return;
    let observer;
    try {
      observer = getRevealObserver();
    } catch (err) {
      // IO unavailable despite the feature check — show everything now
      fresh.forEach(function (el) { pendingShowPlain.add(el); });
      scheduleRevealFlush();
      return;
    }
    for (let i = 0; i < fresh.length; i += 1) observer.observe(fresh[i]);
  }

  // Re-observe nodes injected after load (theme editor re-renders, dynamic
  // sections, infinite-scroll pages). Only the ADDED subtrees are scanned,
  // rAF-batched: at most one scan pass per frame.
  function watchRevealMutations() {
    if (revealMo || !('MutationObserver' in window)) return;
    revealMo = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i += 1) {
        const added = mutations[i].addedNodes;
        for (let k = 0; k < added.length; k += 1) {
          if (added[k].nodeType === 1) pendingRoots.add(added[k]);
        }
      }
      if (pendingRoots.size && !revealMoFrame) {
        revealMoFrame = requestAnimationFrame(function () {
          revealMoFrame = 0;
          const roots = Array.from(pendingRoots);
          pendingRoots.clear();
          for (let i = 0; i < roots.length; i += 1) {
            if (roots[i].isConnected) initReveal(roots[i]);
          }
        });
      }
    });
    revealMo.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------ *
   * Part 6b — button ripple pointer vars (Jlood custom-button,
   * custom-elements.js:1 + buttons.css:44-92; CSS scaffold in base.css §8).
   * Desktop pointers only — (hover: hover) and (pointer: fine) and
   * >=1024 like Jlood's MIN_DEVICE_WIDTH.desktop gate. Writes
   * --cm-mouse-x/--cm-mouse-y in px on .cm-btn--primary/--secondary;
   * X is measured from the INLINE-START edge (RTL-aware via CMConfig.rtl)
   * to feed the logical inset-inline-start in the CSS. rAF-throttled;
   * vars are removed on pointerleave (circle falls back to centre).
   * ------------------------------------------------------------ */
  const RIPPLE_SELECTOR = '.cm-btn--primary, .cm-btn--secondary';
  const rippleMQ = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  let rippleBtn = null;
  let rippleEvt = null;
  let rippleFrame = 0;

  function rippleSupported() {
    return !!rippleMQ && rippleMQ.matches && (window.innerWidth || 0) >= 1024;
  }

  function rippleApply() {
    rippleFrame = 0;
    if (!rippleBtn || !rippleEvt) return;
    const rect = rippleBtn.getBoundingClientRect();
    const rtl = CFG.rtl === true || document.documentElement.getAttribute('dir') === 'rtl';
    const x = rtl ? rect.right - rippleEvt.clientX : rippleEvt.clientX - rect.left;
    rippleBtn.style.setProperty('--cm-mouse-x', x.toFixed(1) + 'px');
    rippleBtn.style.setProperty('--cm-mouse-y', (rippleEvt.clientY - rect.top).toFixed(1) + 'px');
  }

  function rippleReset() {
    if (rippleBtn) {
      rippleBtn.removeEventListener('pointermove', rippleOnMove);
      rippleBtn.removeEventListener('pointerleave', rippleReset);
      rippleBtn.style.removeProperty('--cm-mouse-x');
      rippleBtn.style.removeProperty('--cm-mouse-y');
    }
    rippleBtn = null;
    rippleEvt = null;
    if (rippleFrame) {
      cancelAnimationFrame(rippleFrame);
      rippleFrame = 0;
    }
  }

  function rippleOnMove(event) {
    rippleEvt = event;
    if (!rippleFrame) rippleFrame = requestAnimationFrame(rippleApply);
  }

  function initRipple() {
    // Delegated: cart drawer / quick view re-render buttons without reload.
    document.addEventListener('pointerover', function (event) {
      const target = event.target instanceof Element ? event.target.closest(RIPPLE_SELECTOR) : null;
      if (!rippleSupported()) {
        if (rippleBtn) rippleReset();
        return;
      }
      if (target && target !== rippleBtn) {
        rippleReset();
        rippleBtn = target;
        rippleEvt = event;
        rippleBtn.addEventListener('pointermove', rippleOnMove);
        rippleBtn.addEventListener('pointerleave', rippleReset);
        if (!rippleFrame) rippleFrame = requestAnimationFrame(rippleApply);
      }
    });
  }

  /* ------------------------------------------------------------ *
   * Part 7 — sticky header metrics + hide on scroll
   * on-scroll-up mode: 15 px threshold; the first scroll event only
   * initialises lastY (no hide/show decision on it).
   * ------------------------------------------------------------ */
  let headerEl = null;
  let headerResizeTimer = null;
  let headerScrollStopTimer = null;
  let headerTicking = false;
  let lastY = null; // null until the first scroll event
  let headerResizeObserver = null;
  let headerListenersBound = false;

  /* PERFORMANCE (QC-OPUS-R1 30 / Gate 0): one rAF-deduped entry point for
     every updateHeaderMetrics caller. Boot, window load, the ResizeObserver
     and the countdown hook can all land in the same frame; each direct call
     forced a full-document layout (the header group loop reads N section
     heights on a 500+-card DOM) — the CPU profile showed 144 ms of main
     thread on collection. Coalescing them to one layout per frame keeps the
     written CSS vars identical. */
  let headerUpdateQueued = false;
  function queueHeaderUpdate() {
    if (headerUpdateQueued) return;
    headerUpdateQueued = true;
    requestAnimationFrame(function () {
      headerUpdateQueued = false;
      updateHeaderMetrics();
    });
  }

  function updateHeaderMetrics() {
    if (!headerEl) return;
    const h = headerEl.offsetHeight;
    const hidden = headerEl.classList.contains('header-up');
    document.documentElement.style.setProperty('--cm-header-height', h + 'px');
    // Second, collapsible copy for sticky followers (Jlood --height-header
    // collapses to 0px while the header hides, so the sticky announcement
    // slides up to the top edge with it).
    document.documentElement.style.setProperty('--cm-header-follow', (hidden ? 0 : h) + 'px');
    const bottom = hidden ? 0 : Math.max(0, Math.round(headerEl.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--cm-header-bottom', bottom + 'px');
    // Total height of the whole header group as currently rendered
    // (announcement bar + scrolling banner + utility bar + header): every
    // .shopify-section the group renders sits between the skip link and
    // <main>; closed or expired ones contribute 0.
    const main = document.getElementById('MainContent');
    let stack = 0;
    if (main) {
      for (let node = main.previousElementSibling; node; node = node.previousElementSibling) {
        if (node.classList.contains('shopify-section')) stack += node.offsetHeight;
      }
    }
    document.documentElement.style.setProperty('--cm-header-stack-height', stack + 'px');
  }

  function hideHeader() {
    if (!headerEl) return;
    if (headerEl.classList.contains('header-up')) return;
    queueHeaderUpdate();
    headerEl.classList.add('header-up');
    document.documentElement.style.setProperty('--cm-header-bottom', '0px');
    document.documentElement.style.setProperty('--cm-header-follow', '0px');
  }

  function showHeader() {
    if (!headerEl) return;
    if (!headerEl.classList.contains('header-up')) return;
    headerEl.classList.remove('header-up');
    document.documentElement.style.setProperty('--cm-header-bottom', headerEl.offsetHeight + 'px');
    document.documentElement.style.setProperty('--cm-header-follow', headerEl.offsetHeight + 'px');
    setTimeout(updateHeaderMetrics, 400);
  }

  function onHeaderScroll() {
    if (headerTicking) return;
    headerTicking = true;
    requestAnimationFrame(function () {
      headerTicking = false;
      if (!headerEl) return;
      const y = Math.max(0, window.scrollY || 0);
      if (lastY === null) { lastY = y; return; }
      if (y <= 0) { showHeader(); lastY = y; return; }
      if (Math.abs(y - lastY) <= 15) return;
      // Hide only when nothing is open: no drawer/modal on the stack and no
      // header panel (search / mega menu) holding data-cm-panel-open.
      if (y > lastY) {
        if (openStack.length === 0 && !headerEl.hasAttribute('data-cm-panel-open')) hideHeader();
      } else {
        showHeader();
      }
      lastY = y;
    });
  }

  // Re-resolves the header element. In the theme editor the header section is
  // re-rendered on shopify:section:load / :reorder, so the old headerEl node
  // becomes detached; rebind the ResizeObserver to the fresh node.
  function bindHeader() {
    const found = document.querySelector('.section-header');
    if (found !== headerEl) {
      if (headerResizeObserver) {
        headerResizeObserver.disconnect();
        headerResizeObserver = null;
      }
      headerEl = found;
      lastY = null;
      if (headerEl && typeof ResizeObserver !== 'undefined') {
        headerResizeObserver = new ResizeObserver(function () {
          queueHeaderUpdate();
        });
        headerResizeObserver.observe(headerEl);
      }
    }
    updateHeaderMetrics();
  }

  function initHeader() {
    bindHeader();
    if (headerListenersBound) return;
    headerListenersBound = true;
    window.addEventListener('resize', function () {
      clearTimeout(headerResizeTimer);
      headerResizeTimer = setTimeout(queueHeaderUpdate, 100);
    });
    window.addEventListener('load', queueHeaderUpdate);
    // The announcement bar closes/shrinks when its countdown ends
    // (offer slides are removed for real, cm-announcement.js) — re-measure
    // once the DOM settles.
    document.addEventListener('cm-countdown:end', function () {
      queueHeaderUpdate();
    });
    document.addEventListener('shopify:section:load', bindHeader);
    document.addEventListener('shopify:section:reorder', bindHeader);
    window.addEventListener('scroll', function () {
      clearTimeout(headerScrollStopTimer);
      headerScrollStopTimer = setTimeout(queueHeaderUpdate, 100);
    }, { passive: true });
    lastY = null;
    window.addEventListener('scroll', onHeaderScroll, { passive: true });
  }

  /* ------------------------------------------------------------ *
   * Part 8 — bootstrap and export
   * ------------------------------------------------------------ */
  const reveal = { init: initReveal };
  const header = { update: updateHeaderMetrics };

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* ------------------------------------------------------------ *
   * Deferred images (PERFORMANCE, QC-OPUS-R1 30). snippets/image.liquid with
   * defer_load: true emits data-cm-src / data-cm-srcset / data-cm-sizes
   * instead of src / srcset / sizes, so hidden hero segments and non-active
   * PDP gallery slides never compete with the LCP image for bandwidth.
   * hydrateImages() moves the data-* values onto the real attributes
   * (<source> first, then <img>, loading=lazy so the browser still gates by
   * viewport). afterLoad() runs a callback once the window has loaded and
   * the main thread is idle.
   * ------------------------------------------------------------ */
  function hydrateImages(root) {
    const scope = root || document;
    const nodes = scope.querySelectorAll('[data-cm-srcset], [data-cm-src]');
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const srcset = node.getAttribute('data-cm-srcset');
      const sizes = node.getAttribute('data-cm-sizes');
      const src = node.getAttribute('data-cm-src');
      if (node.tagName === 'IMG') node.setAttribute('loading', 'lazy');
      if (sizes) node.setAttribute('sizes', sizes);
      if (srcset) node.setAttribute('srcset', srcset);
      if (src) node.setAttribute('src', src);
      node.removeAttribute('data-cm-srcset');
      node.removeAttribute('data-cm-sizes');
      node.removeAttribute('data-cm-src');
    }
    return nodes.length;
  }

  function afterLoad(fn) {
    const run = function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 2000 });
      else window.setTimeout(fn, 200);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  window.CM = {
    __loaded: true,
    on: on,
    off: off,
    emit: emit,
    fetchSection: fetchSection,
    parseHTML: parseHTML,
    cart: cart,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    trapFocus: trapFocus,
    open: open,
    close: close,
    closeAll: closeAll,
    reveal: reveal,
    header: header,
    hydrateImages: hydrateImages,
    afterLoad: afterLoad,
    config: CFG
  };

  CM.on('reveal:refresh', function (detail) {
    initReveal((detail && detail.root) || document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target || document);
  });

  function boot() {
    initReveal(document);
    initRipple();
    initHeader();
    CM.emit('ready', {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
